/**
 * RaceProgressPanel — HUD overlay showing the local player's race progress.
 *
 * Displays checkpoint progress, lap count, place, and finish status.
 * Only visible during a live race event when the local player has
 * race state.
 *
 * Phase gate reads the ROOM SCHEMA (state.gmEvent.phase), NOT the
 * announcement broadcast: announcements auto-clear after a few seconds
 * (GmEventService ANNOUNCEMENT_TIMEOUT_MS), which used to blank this panel
 * for the rest of the race (A.6 — same class of bug as the lobby HUD's
 * room-create race; the schema is always readable late).
 *
 * @see docs/design/gm/GM_EVENT_PHASE4_SPEC.md §4.1
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

function getLocalSessionId(): string | null {
  return (window as any).nm?.getRoom?.()?.sessionId ?? null;
}

/** Live phase from the room schema (null when no event). */
function getSchemaGmEventPhase(): string | null {
  const ge = (window as any).nm?.getRoom?.()?.state?.gmEvent;
  return ge && ge.id ? String(ge.phase) : null;
}

function formatTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  return mins > 0
    ? `${mins}:${secs.toString().padStart(2, '0')}.${tenths}`
    : `${secs}.${tenths}s`;
}

export const RaceProgressPanel: React.FC = () => {
  const { racePlayerState } = useGmEvent();

  // Schema-first: the announcement auto-clears mid-race; the schema doesn't.
  if (getSchemaGmEventPhase() !== 'live') return null;
  if (!racePlayerState) return null;

  const localSid = getLocalSessionId();
  if (racePlayerState.sessionId !== localSid) return null;

  const progress = racePlayerState.totalCheckpoints > 0
    ? `${racePlayerState.checkpointIndex + 1}/${racePlayerState.totalCheckpoints}`
    : '—';
  const placeLabel = racePlayerState.place ? `#${racePlayerState.place}` : '';
  const isFinished = racePlayerState.status === 'finished';
  const isDnf = racePlayerState.status === 'dnf';

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    // Below the minimap (fixed top:10, 180×140 + frame ≈ y10-160, z1000):
    // minimap bottom + ~12px gutter, so the panel never tucks under it
    // (A.6 VQA nit — CLIENT_FEEL §6.2).
    top: 172,
    right: 20,
    padding: '8px 14px',
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontFamily: FONTS.ui,
    zIndex: 100,
    pointerEvents: 'none',
    minWidth: 140,
  };

  return (
    <div style={containerStyle}>
      <div style={{ fontSize: 14, fontWeight: 'bold', color: COLORS.textAccent }}>
        Lap {racePlayerState.lap + (isFinished ? 0 : 1)}
      </div>
      <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
        Checkpoint: {progress}
      </div>
      {placeLabel && (
        <div style={{ fontSize: 13, fontWeight: 'bold', color: COLORS.textAccent }}>
          Place: {placeLabel}
        </div>
      )}
      {isFinished && racePlayerState.finishTimeMs !== undefined && (
        <div style={{ fontSize: 12, color: COLORS.success }}>
          Finished! {formatTime(racePlayerState.finishTimeMs)}
        </div>
      )}
      {isDnf && (
        <div style={{ fontSize: 12, color: COLORS.danger }}>
          DNF
        </div>
      )}
      {racePlayerState.status === 'waiting' && (
        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
          Waiting for start...
        </div>
      )}
    </div>
  );
};
